import { useState } from 'react';
import { Modal } from '@mantine/core';
import Button from '@ui/components/Button/Button';
import Icon from '@ui/components/Icon/Icon';
import useDelayedOpening from 'src/new_arch/hooks/useDelayedOpening';
import useStackeableModal from 'src/new_arch/hooks/useStackeableModal';

import './ScimKeyAvailableModal.scss';

const MODAL_STYLES = {
	overlay: { zIndex: 210 },
	inner: { zIndex: 211 },
	content: { backgroundColor: 'var(--color-bg-container)' },
	header: {
		padding: '10px 16px',
		borderBottom: '1px solid var(--color-outline-default)',
		backgroundColor: 'var(--color-bg-container)'
	},
	title: { color: 'var(--color-text-primary)' },
	body: { padding: '24px', backgroundColor: 'var(--color-bg-container)' }
};

type Props = {
	token: string;
	onClose: () => void;
	stackOrder?: number;
};

const ScimKeyAvailableModal = ( { token, onClose, stackOrder = 1 }: Props ) => {
	const opened = useDelayedOpening();
	const { modalStyles } = useStackeableModal( { stackOrder, defaultStyles: MODAL_STYLES } );
	const [ copied, setCopied ] = useState( false );

	const handleCopy = () => {
		navigator.clipboard.writeText( token );
		setCopied( true );
		setTimeout( () => setCopied( false ), 2000 );
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title="SCIM key available"
			withCloseButton={false}
			size={420}
			styles={modalStyles}
		>
			<div className="ScimKeyAvailableModal">
				<p style={{ color: 'var(--color-text-primary)', fontSize: 14, lineHeight: '22px', marginBottom: 15 }}>
					Copy your SCIM key and endpoint to your identity provider.
				</p>

				<div className="ScimKeyAvailableModal-token-box">
					<div className="ScimKeyAvailableModal-token-box-header">
						<span className="ScimKeyAvailableModal-token-box-label">Bearer Token</span>
						<button
							className={`ScimKeyAvailableModal-token-box-copy-btn${copied ? ' ScimKeyAvailableModal-token-box-copy-btn--copied' : ''}`}
							onClick={handleCopy}
						>
							{copied ? 'Copied' : 'Copy'}
							{copied
								? <Icon icon="check" size={16} />
								: (
									<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M10.6663 0.667969H2.66634C1.93301 0.667969 1.33301 1.26797 1.33301 2.0013V11.3346H2.66634V2.0013H10.6663V0.667969ZM12.6663 3.33464H5.33301C4.59967 3.33464 3.99967 3.93464 3.99967 4.66797V14.0013C3.99967 14.7346 4.59967 15.3346 5.33301 15.3346H12.6663C13.3997 15.3346 13.9997 14.7346 13.9997 14.0013V4.66797C13.9997 3.93464 13.3997 3.33464 12.6663 3.33464ZM12.6663 14.0013H5.33301V4.66797H12.6663V14.0013Z" fill="var(--color-icon-secondary-dark)" />
									</svg>
								)}
						</button>
					</div>
					<input
						className="ScimKeyAvailableModal-token-box-value"
						value={token}
						readOnly
						spellCheck={false}
						onFocus={( event ) => { event.target.scrollLeft = 0; }}
					/>
				</div>

				<div className="ScimKeyAvailableModal-warning" style={{ marginTop: 15 }}>
					<Icon icon="warning" size={12} style={{ flexShrink: 0, marginTop: 3 }} />
					<span>Save this key in a password manager. You won&apos;t be able to view it again.</span>
				</div>

				<div className="ScimKeyAvailableModal-footer">
					<Button variant="subtle" color="dark" onClick={onClose}>Cancel</Button>
					<Button variant="filled" onClick={onClose}>Done</Button>
				</div>
			</div>
		</Modal>
	);
};

export default ScimKeyAvailableModal;
